import React, { useState } from "react";
import { motion } from "framer-motion";
import { InputField } from "@cakrawalajakarta/shared-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxOpen, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { formatScheduleDate, jotr2026Schedule } from "../data/jotr2026Schedule";

export const Schedule = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const filteredRundown = jotr2026Schedule.filter((entry) =>
    Object.values(entry).some((value) =>
      value.toString().toLowerCase().includes(searchQuery.toLowerCase()),
    ),
  );

  const groupedRundown = filteredRundown.reduce((acc, entry) => {
    acc[entry.date] = acc[entry.date] || [];
    acc[entry.date].push(entry);
    return acc;
  }, {});

  return (
    <section
      id="schedule"
      className="w-full min-h-screen py-20 text-center text-lime-600 flex-col justify-center relative bg-white"
    >
      <div className="relative z-0 container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-6xl mx-auto space-y-4">
            {/* RPC — TBC */}
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-2xl px-6 py-8 md:py-12 border border-gray-200 flex flex-col items-center justify-center">
                <FontAwesomeIcon
                  icon={faBoxOpen}
                  className="text-4xl text-lime-600 mb-4"
                />
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Decathlon+Semanggi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg text-lime-600 font-bold mb-3 flex items-center gap-2 hover:underline"
                >
                  <FontAwesomeIcon icon={faLocationDot} />
                  Race Pack Collection
                </a>
                <p className="text-xl text-lime-800">Decathlon JKT</p>
                <p className="text-sm text-gray-600">July 18 - 19, 2026</p>
              </div>

              <div className="bg-white rounded-2xl px-6 py-8 md:py-12 border border-gray-200 flex flex-col items-center justify-center">
                <FontAwesomeIcon
                  icon={faBoxOpen}
                  className="text-4xl text-lime-600 mb-4"
                />
                <a
                  href="https://www.google.com/maps/search/?api=1&query=TNGGP+Hall+Cibodas"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg text-lime-600 font-bold mb-3 flex items-center gap-2 hover:underline"
                >
                  <FontAwesomeIcon icon={faLocationDot} />
                  On-Site Race Pack Collection & Medical Check (75K)
                </a>
                <p className="text-xl text-lime-800">TNGGP Hall</p>
                <p className="text-sm text-gray-600">July 24 - 25, 2026</p>
              </div>
            </motion.div>

            {/* Table */}
            <motion.div
              className="bg-white rounded-2xl border border-gray-200 text-left relative overflow-hidden max-w-6xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              viewport={{ once: true }}
            >
              <div className="flex flex-col md:flex-row justify-between items-center mb-4 px-4 pt-4 gap-4">
                <div className="text-left">
                  <h3 className="text-xl font-bold text-lime-600 uppercase">
                    Event Schedule (July 18 – 26, 2026)
                  </h3>
                  <p className="text-sm text-gray-500 italic mt-1">
                    Latest schedule from the event organizer. Subject to change.
                  </p>
                </div>
                <div className="w-full max-w-xs">
                  <InputField
                    name="search"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search schedule..."
                    labelColor="text-lime-800"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full table-auto divide-y divide-gray-200">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Activity
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Location
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(groupedRundown).length > 0 ? (
                      Object.entries(groupedRundown).map(([date, entries]) =>
                        entries.map((entry, idx) => {
                          const isCutOff =
                            entry.activity.includes("Cut Off Time");
                          const isFlagOff = entry.activity.includes("Flag Off");
                          const rowColor = isFlagOff
                            ? "text-lime-600"
                            : isCutOff
                              ? "text-red-600"
                              : "text-gray-600";

                          return (
                            <tr
                              key={`${date}-${idx}`}
                              className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50"
                            >
                              <td className="px-4 py-4 text-sm text-center md:text-left text-gray-600">
                                {idx === 0 ? formatScheduleDate(date) : ""}
                              </td>
                              <td className="px-4 py-4 text-sm text-left text-gray-600">
                                {entry.time}
                              </td>
                              <td
                                className={`px-4 py-4 text-sm text-left ${rowColor}`}
                              >
                                {entry.activity}
                              </td>
                              <td className="px-4 py-4 text-sm text-left text-gray-600">
                                {entry.location}
                              </td>
                            </tr>
                          );
                        }),
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-4 py-4 text-sm text-gray-600 text-center"
                        >
                          No events found matching your search.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
